import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Play, Search, Bookmark, BookmarkCheck, Clock, Eye, CheckCircle2,
  Radio, CalendarClock, Sparkles, X, MessageCircle, ExternalLink, Pin,
  ArrowUpDown, Tag as TagIcon, FileText, Link as LinkIcon, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import Watermark from "@/components/Watermark";
import { WEBINARS } from "@/constants/testIds";

// Discord Connect destination — OAuth-only flow. The old raw-invite
// pattern was retired to eliminate role-less "orphan" joins.
const DISCORD_CONNECT_URL = "/account?tab=discord";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function formatDuration(sec, t) {
  if (!sec || sec <= 0) return t("webinarsUi.durationEmpty");
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return t("webinarsUi.durationHoursMinutes", { h, m });
  return t("webinarsUi.durationMinutes", { m });
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "";
  }
}

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetIso) return null;
  const diff = new Date(targetIso).getTime() - now;
  if (diff <= 0) return { done: true, d: 0, h: 0, m: 0, s: 0 };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { done: false, d, h, m, s };
}

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

function CountdownCard({ webinar }) {
  const { t } = useTranslation();
  const cd = useCountdown(webinar?.scheduled_at);
  if (!webinar || !cd) return null;
  return (
    <div
      data-testid={WEBINARS.nextCard}
      className="relative overflow-hidden rounded-2xl bg-slate-950 text-white p-6 border border-amber-500/25"
    >
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-amber-500/15 blur-3xl" />
      <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold">
            <CalendarClock size={12} /> {t("webinarsUi.nextLiveSession")}
          </div>
          <h3 className="mt-2 font-display font-bold text-2xl text-white tracking-tight">
            {webinar.title}
          </h3>
          {webinar.description ? (
            <p className="mt-2 text-sm text-slate-300 max-w-2xl leading-relaxed">
              {webinar.description}
            </p>
          ) : null}
          <p className="mt-3 text-xs text-slate-400 font-mono">
            {formatDate(webinar.scheduled_at)} · {new Date(webinar.scheduled_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
        <div className="grid grid-cols-4 gap-2 shrink-0">
          {[
            { labelKey: "webinarsUi.countdownDays", value: cd.d },
            { labelKey: "webinarsUi.countdownHrs",  value: cd.h },
            { labelKey: "webinarsUi.countdownMin",  value: cd.m },
            { labelKey: "webinarsUi.countdownSec",  value: cd.s },
          ].map((u) => (
            <div key={u.labelKey} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-center min-w-[62px]">
              <div className="font-mono font-black text-2xl text-amber-400 tabular-nums">
                {String(u.value).padStart(2, "0")}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold mt-0.5">
                {t(u.labelKey)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LiveNowBanner({ webinar, onOpen }) {
  const { t } = useTranslation();
  if (!webinar) return null;
  return (
    <div
      data-testid={WEBINARS.liveBanner}
      className="relative overflow-hidden rounded-2xl border border-rose-500/40 bg-gradient-to-br from-rose-950 via-slate-950 to-slate-950 text-white p-6"
    >
      <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-rose-300 font-bold">
              {t("webinarsUi.liveNow")}
            </span>
          </div>
          <h3 className="mt-2 font-display font-bold text-2xl text-white tracking-tight">
            {webinar.title}
          </h3>
          {webinar.description ? (
            <p className="mt-1.5 text-sm text-slate-300 max-w-2xl">
              {webinar.description}
            </p>
          ) : null}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            onClick={() => onOpen(webinar)}
            data-testid={WEBINARS.liveJoinButton}
            size="lg"
            className="bg-rose-500 hover:bg-rose-400 text-white font-bold h-12 px-6"
          >
            <Radio size={16} className="mr-2" /> {t("webinarsUi.joinLive")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TagChip({ tag, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`${WEBINARS.tagChip}-${tag}`}
      className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
      }`}
    >
      <TagIcon size={10} /> {tag}
    </button>
  );
}

// Grouped filter panel — clusters 30+ tag chips into 5 semantic buckets
// so the sidebar doesn't feel like a wall of text. The MAP is authored
// lowercase-normalized for case-insensitive matching, and any tag that
// doesn't match a group falls into "Other" so we never silently drop a
// chip when the CMS adds a new one.
const FILTER_GROUPS = {
  marketsKey:   ["Indices", "Forex", "Equities", "Commodities", "Bonds", "CFDs", "Options"],
  analysisKey:  ["Charting", "Order Flow", "Correlation", "Timeframe Correlation", "Ranges", "Trend", "Volatility", "Liquidity", "Traps", "News", "Macro", "Watchlists"],
  executionKey: ["Setup", "Entries", "Execution", "Playbook", "Backtesting", "Sessions", "Brokers", "Tools", "Math", "Live", "Hedging", "Portfolio"],
  riskKey:      ["Risk Sizing", "Drawdowns"],
  mindsetKey:   ["Psychology", "Prep", "Process", "Lifestyle", "Journaling", "Prop"],
};

function groupTags(tags) {
  const lut = new Map();
  Object.entries(FILTER_GROUPS).forEach(([group, list]) => {
    list.forEach((t) => lut.set(t.toLowerCase(), group));
  });
  const out = { marketsKey: [], analysisKey: [], executionKey: [], riskKey: [], mindsetKey: [], otherKey: [] };
  tags.forEach((t) => {
    const g = lut.get(String(t).toLowerCase()) || "otherKey";
    out[g].push(t);
  });
  // Alphabetize within each group for a clean read.
  Object.keys(out).forEach((g) => out[g].sort((a, b) => a.localeCompare(b)));
  return out;
}

function FilterPanel({ tags, active, onToggle, onClear, hasSearch }) {
  const { t } = useTranslation();
  const grouped = groupTags(tags);
  const hasActive = active.length > 0 || hasSearch;
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5 space-y-4">
      {/* Header + clear-all button */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
          {t("webinarsUi.filterBy")}
        </span>
        {hasActive && (
          <button
            type="button"
            onClick={onClear}
            data-testid={WEBINARS.clearFilters}
            className="text-xs font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <X size={12} /> {t("webinarsUi.clearFilters", { count: active.length })}
          </button>
        )}
      </div>

      {/* Active-filter pills — always visible at the top so the user
          sees exactly what's selected without scanning every group. */}
      {active.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-slate-200">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mr-1">
            {t("webinarsUi.activeFilters")}
          </span>
          {active.map((t) => (
            <button
              key={`active-${t}`}
              type="button"
              onClick={() => onToggle(t)}
              className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 bg-blue-800 text-white hover:bg-blue-900 transition-colors"
            >
              {t} <X size={11} />
            </button>
          ))}
        </div>
      )}

      {/* Grouped tag rows — each group only renders if it actually has
          chips, so the panel stays tight for smaller tag sets. */}
      <div className="space-y-3">
        {Object.entries(grouped).map(([group, groupTagList]) => (
          groupTagList.length === 0 ? null : (
            <div key={group} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
              <div className="w-full sm:w-24 shrink-0 text-[10px] uppercase tracking-widest font-bold text-slate-600 pt-1.5">
                {t(`webinarsUi.${group}`)}
              </div>
              <div className="flex flex-wrap gap-1.5 flex-1">
                {groupTagList.map((t) => (
                  <TagChip key={t} tag={t} active={active.includes(t)} onClick={() => onToggle(t)} />
                ))}
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

function WebinarCard({ webinar, onOpen, onBookmark }) {
  const { t } = useTranslation();
  const thumb = webinar.thumbnail_url
    || (webinar.vimeo_id && /^\d+$/.test(webinar.vimeo_id)
        ? `https://vumbnail.com/${webinar.vimeo_id}.jpg`
        : null);

  return (
    <div
      data-testid={WEBINARS.card}
      className="group relative bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300"
    >
      {/* Thumbnail */}
      <button
        type="button"
        onClick={() => onOpen(webinar)}
        className="relative block aspect-video bg-slate-900 overflow-hidden"
        aria-label={t("webinarsUi.watchAria", { title: webinar.title })}
      >
        {thumb ? (
          <img
            src={thumb}
            alt={webinar.title}
            loading="lazy"
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950">
            <Play size={40} className="text-white/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
            <Play size={22} className="text-slate-900 fill-slate-900 ml-1" />
          </div>
        </div>
        {webinar.is_pinned && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-400 bg-slate-950/80 backdrop-blur px-2 py-1 rounded">
            <Pin size={10} /> {t("webinarsUi.featured")}
          </div>
        )}
        {webinar.watched && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-400 bg-slate-950/80 backdrop-blur px-2 py-1 rounded">
            <CheckCircle2 size={10} /> {t("webinarsUi.watched")}
          </div>
        )}
        {webinar.duration_seconds > 0 && (
          <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-[11px] font-mono font-semibold text-white bg-slate-950/80 backdrop-blur px-2 py-0.5 rounded">
            <Clock size={10} /> {formatDuration(webinar.duration_seconds, t)}
          </div>
        )}
      </button>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-bold text-base text-slate-900 leading-snug line-clamp-2">
            {webinar.title}
          </h3>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onBookmark(webinar); }}
            data-testid={`${WEBINARS.bookmarkButton}-${webinar.id}`}
            className="shrink-0 text-slate-400 hover:text-amber-500 transition-colors"
            aria-label={webinar.bookmarked ? t("webinarsUi.removeBookmark") : t("webinarsUi.addBookmark")}
          >
            {webinar.bookmarked
              ? <BookmarkCheck size={18} className="text-amber-500 fill-amber-500" />
              : <Bookmark size={18} />}
          </button>
        </div>

        {webinar.description && (
          <p className="mt-1.5 text-xs text-slate-500 line-clamp-2 leading-relaxed">
            {webinar.description}
          </p>
        )}

        {webinar.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {webinar.tags.slice(0, 4).map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500 font-medium">
          <span>{formatDate(webinar.recorded_at) || formatDate(webinar.created_at)}</span>
          <span className="inline-flex items-center gap-1">
            <Eye size={11} /> {webinar.view_count?.toLocaleString?.() || 0}
          </span>
        </div>
      </div>
    </div>
  );
}

function WebinarPlayer({ webinar, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!webinar) return;
    api.post(`/webinars/${webinar.id}/view`).catch(() => {});
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [webinar, onClose]);

  if (!webinar) return null;

  const isLive = webinar.status === "live";
  const vimeoSrc = webinar.vimeo_id && /^\d+$/.test(webinar.vimeo_id)
    ? `https://player.vimeo.com/video/${webinar.vimeo_id}?title=0&byline=0&portrait=0`
    : null;

  return (
    <div
      data-testid={WEBINARS.playerModal}
      className="fixed inset-0 z-[80] bg-slate-950/90 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-screen flex items-start justify-center py-8 px-4">
        <div
          className="w-full max-w-5xl bg-white rounded-2xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="min-w-0 flex-1">
              {isLive && (
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-rose-600 mb-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" /> {t("webinarsUi.liveNow")}
                </span>
              )}
              <h3 className="font-display font-bold text-lg text-slate-900 truncate">
                {webinar.title}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid={WEBINARS.playerClose}
              className="ml-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center shrink-0"
              aria-label={t("webinarsUi.close")}
            >
              <X size={18} />
            </button>
          </div>

          {/* Video area */}
          <div className="aspect-video bg-slate-950 relative">
            <Watermark>
            {isLive && webinar.live_url ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-white p-8">
                <Radio size={40} className="text-rose-500 animate-pulse" />
                <p className="text-center text-sm text-slate-300 max-w-md">
                  {t("webinarsUi.liveStreamingExternal")}
                </p>
                <a
                  href={webinar.live_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={WEBINARS.playerLiveLink}
                  className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-400 text-white font-bold px-6 py-3 rounded-lg"
                >
                  {t("webinarsUi.openLiveStream")} <ExternalLink size={14} />
                </a>
              </div>
            ) : vimeoSrc ? (
              <iframe
                src={vimeoSrc}
                title={webinar.title}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
                {t("webinarsUi.noVideoAttached")}
              </div>
            )}
            </Watermark>
          </div>

          {/* Meta + notes */}
          <div className="p-6 space-y-5">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 font-medium">
              <span className="inline-flex items-center gap-1">
                <Clock size={12} /> {formatDuration(webinar.duration_seconds)}
              </span>
              <span className="inline-flex items-center gap-1">
                <Eye size={12} /> {webinar.view_count?.toLocaleString?.() || 0} views
              </span>
              {webinar.recorded_at && (
                <span>{t("webinarsUi.recorded", { date: formatDate(webinar.recorded_at) })}</span>
              )}
            </div>

            {webinar.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {webinar.tags.map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-700 px-2 py-1 rounded">
                    {t}
                  </span>
                ))}
              </div>
            )}

            {webinar.description && (
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                {webinar.description}
              </p>
            )}

            {webinar.notes && (
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2 inline-flex items-center gap-1.5">
                  <FileText size={12} /> {t("webinarsUi.sessionNotes")}
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {webinar.notes}
                </div>
              </div>
            )}

            {webinar.resources?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2 inline-flex items-center gap-1.5">
                  <LinkIcon size={12} /> {t("webinarsUi.attachedResources")}
                </div>
                <ul className="space-y-1.5">
                  {webinar.resources.map((r, i) => (
                    <li key={i}>
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-blue-800 hover:text-blue-700"
                      >
                        <LinkIcon size={13} /> {r.title || r.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main tab                                                                  */
/* -------------------------------------------------------------------------- */

export default function Webinars() {
  const { t } = useTranslation();
  const [live, setLive] = useState(null);
  const [next, setNext] = useState(null);
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTags, setActiveTags] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(true);
  const [openedWebinar, setOpenedWebinar] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (kind) params.kind = kind;
      if (q.trim()) params.q = q.trim();
      if (activeTags.length) params.tags = activeTags.join(",");
      const r = await api.get("/webinars", { params });
      setItems(r.data.items || []);
    } catch (e) {
      if (e.response?.status !== 403) {
        toast.error(t("webinarsUi.errorLoadWebinars"));
      }
    } finally {
      setLoading(false);
    }
  }, [q, sort, activeTags, kind]);

  useEffect(() => {
    api.get("/webinars/live").then((r) => setLive(r.data.webinar)).catch(() => {});
    api.get("/webinars/next").then((r) => setNext(r.data.webinar)).catch(() => {});
    api.get("/webinars/tags").then((r) => setTags(r.data.tags || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const toggleTag = (t) => {
    setActiveTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const clearFilters = () => {
    setActiveTags([]);
    setQ("");
  };

  const bookmarkToggle = async (w) => {
    try {
      const r = await api.post(`/webinars/${w.id}/bookmark`);
      setItems((prev) => prev.map((x) => x.id === w.id ? { ...x, bookmarked: r.data.bookmarked } : x));
      toast.success(r.data.bookmarked ? t("webinarsUi.bookmarkSaved") : t("webinarsUi.bookmarkRemoved"));
    } catch {
      toast.error(t("webinarsUi.errorUpdateBookmark"));
    }
  };

  const openWebinar = (w) => {
    setOpenedWebinar(w);
    // Optimistically mark as watched in the list
    setItems((prev) => prev.map((x) => x.id === w.id
      ? { ...x, watched: true, view_count: (x.view_count || 0) + 1 }
      : x));
  };

  const featured = useMemo(() => items.filter((x) => x.is_pinned), [items]);
  const rest = useMemo(() => items.filter((x) => !x.is_pinned), [items]);

  return (
    <div data-testid={WEBINARS.root} className="space-y-6">
      {/* Live banner */}
      {live && <LiveNowBanner webinar={live} onOpen={openWebinar} />}

      {/* Next countdown */}
      {next && <CountdownCard webinar={next} />}

      <div className="flex flex-wrap gap-2" role="tablist" data-testid="video-library-kind-filter">
        {["", "webinar", "recap", "video"].map((k) => (
          <button key={k || "all"} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)}
            data-testid={`video-library-kind-${k || "all"}`}
            className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${kind === k ? "bg-blue-900 border-blue-900 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-blue-800 hover:text-blue-800"}`}>
            {t(`webinarsUi.kind_${k || "all"}`)}
          </button>
        ))}
      </div>

      {/* Search + Sort + Discord CTA */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("webinarsUi.searchPlaceholder")}
            data-testid={WEBINARS.searchInput}
            className="h-11 pl-9 bg-white border-slate-300 focus-visible:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
            {t("webinarsUi.sort")}
          </label>
          <div className="relative">
            <ArrowUpDown size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid={WEBINARS.sortSelect}
              className="h-11 pl-8 pr-3 rounded-md border border-slate-300 bg-white text-sm font-semibold text-slate-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
            >
              <option value="newest">{t("webinarsUi.sortNewest")}</option>
              <option value="oldest">{t("webinarsUi.sortOldest")}</option>
              <option value="longest">{t("webinarsUi.sortLongest")}</option>
              <option value="most_watched">{t("webinarsUi.sortMostWatched")}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tag filter chips — grouped by category so 30+ tags don't feel
          like a wall. Selected tags surface at the top as removable
          pills, so the user always sees what's active without hunting
          for the highlighted chips in the grid. */}
      {tags.length > 0 && (
        <FilterPanel
          tags={tags}
          active={activeTags}
          onToggle={toggleTag}
          onClear={clearFilters}
          hasSearch={!!q}
        />
      )}

      {/* Discord CTA — replaces idea submissions */}
      <div
        data-testid={WEBINARS.discordCta}
        className="relative overflow-hidden rounded-xl border border-indigo-500/25 bg-gradient-to-br from-indigo-950 to-slate-950 text-white px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center shrink-0">
            <MessageCircle size={18} className="text-indigo-300" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-indigo-300 font-semibold">
              {t("webinarsUi.gotTopicInMind")}
            </div>
            <p className="mt-0.5 text-sm text-slate-200 leading-relaxed">
              {t("webinarsUi.suggestTopicDescription")}
            </p>
          </div>
        </div>
        <Link
          to={DISCORD_CONNECT_URL}
          data-testid={WEBINARS.discordButton}
          className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold text-sm h-10 px-5 rounded-lg shrink-0 transition-colors"
        >
          {t("webinarsUi.openDiscord")} <ExternalLink size={13} />
        </Link>
      </div>

      {/* Featured row */}
      {featured.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Pin size={14} className="text-amber-600" />
            <h2 className="font-display font-bold text-sm uppercase tracking-widest text-slate-700">
              {t("webinarsUi.featured")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {featured.map((w) => (
              <WebinarCard key={w.id} webinar={w} onOpen={openWebinar} onBookmark={bookmarkToggle} />
            ))}
          </div>
        </section>
      )}

      {/* Library */}
      <section data-testid={WEBINARS.library}>
        {featured.length > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <Play size={14} className="text-blue-800" />
            <h2 className="font-display font-bold text-sm uppercase tracking-widest text-slate-700">
              {t("webinarsUi.library")}
            </h2>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> {t("webinarsUi.loadingWebinars")}
          </div>
        ) : rest.length === 0 && featured.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
            <Sparkles size={28} className="mx-auto text-slate-400 mb-3" />
            <p className="text-sm text-slate-600 font-semibold">{t("webinarsUi.noWebinarsMatch")}</p>
            <p className="text-xs text-slate-500 mt-1">{t("webinarsUi.tryClearingFilters")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {rest.map((w) => (
              <WebinarCard key={w.id} webinar={w} onOpen={openWebinar} onBookmark={bookmarkToggle} />
            ))}
          </div>
        )}
      </section>

      {/* Player modal */}
      <WebinarPlayer webinar={openedWebinar} onClose={() => setOpenedWebinar(null)} />
    </div>
  );
}
